import { type FunctionComponent, type MouseEvent, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { PageLinksArrowItem } from './PageLinksArrowItem.tsx';
import { PageLinksPageItem } from './PageLinksPageItem.tsx';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import { NumberInput } from 'dibs-elements/exports/NumberInput.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import styles from './styles/PageLinksItem.scss';

import { type LinkType } from './getLinks.ts';

type PageLinksItemExternalProps = {
    currentPage: number;
    getPageLinkUrl?: (pageNumber: number) => string;
    isSelected?: boolean;
    numberOfPages: number;
    onLinkClick: (args: { e: MouseEvent<HTMLElement>; pageNumber: number }) => void;
    pageNumber?: number;
    type: LinkType;
    noUnderline?: boolean;
};

export const PageLinksItem: FunctionComponent<PageLinksItemExternalProps> = ({
    currentPage,
    getPageLinkUrl,
    isSelected,
    numberOfPages,
    onLinkClick,
    pageNumber = 1,
    type,
    noUnderline,
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [inputValue, setInputValue] = useState<number | null>(null);

    switch (type) {
        case 'BACK': {
            const href = getPageLinkUrl ? getPageLinkUrl(currentPage - 1) : undefined;
            return (
                <PageLinksArrowItem
                    isLeft
                    href={href}
                    onClick={(e: MouseEvent<HTMLElement>) =>
                        onLinkClick({ e, pageNumber: currentPage - 1 })
                    }
                    dataTn={`page-${type}`}
                >
                    <FormattedMessage id="PageLinks.previous" defaultMessage="Prev" />
                </PageLinksArrowItem>
            );
        }
        case 'PAGE': {
            const dataTn = `page-${pageNumber}${isSelected ? '-selected' : ''}`;
            const href = getPageLinkUrl ? getPageLinkUrl(pageNumber) : undefined;
            return (
                <PageLinksPageItem
                    href={href}
                    isSelected={isSelected}
                    dataTn={dataTn}
                    noUnderline={noUnderline}
                    onClick={e => onLinkClick({ e, pageNumber })}
                >
                    <FormattedNumber value={pageNumber} />
                </PageLinksPageItem>
            );
        }
        case 'ELLIPSIS': {
            return (
                <div
                    style={{
                        position: 'relative',
                    }}
                >
                    <Link
                        onClick={() => {
                            setShowTooltip(!showTooltip);
                        }}
                        className={styles.ellipsis}
                        underline={noUnderline ? 'none' : undefined}
                    >
                        &hellip;
                    </Link>
                    <Tooltip
                        isVisible={showTooltip}
                        closeOnOutsideClick
                        align="center"
                        direction="top"
                        paddingSize="xSmall"
                    >
                        <div className={styles.tooltipContents}>
                            <div>
                                <NumberInput
                                    autoFocus
                                    size={'small'}
                                    dataTn={'page-link-tooltip-input'}
                                    label={
                                        <FormattedMessage
                                            id="PageLinks.pageNumber"
                                            defaultMessage="Page Number"
                                        />
                                    }
                                    onChange={val => setInputValue(val)}
                                    value={inputValue}
                                />
                            </div>
                            <div className={styles.tooltipGoButton}>
                                <Button
                                    dataTn="page-link-tooltip-submit"
                                    type="secondary"
                                    size="small"
                                    onClick={(e: MouseEvent<HTMLElement>) => {
                                        const newPage = inputValue || 0;
                                        if (newPage >= 1 && newPage <= numberOfPages) {
                                            onLinkClick({ e, pageNumber: newPage });
                                            setShowTooltip(false);
                                        }
                                    }}
                                >
                                    <FormattedMessage id="PageLinks.go" defaultMessage="Go" />
                                </Button>
                            </div>
                        </div>
                    </Tooltip>
                </div>
            );
        }
        case 'FORWARD': {
            const href = getPageLinkUrl ? getPageLinkUrl(currentPage + 1) : undefined;
            return (
                <PageLinksArrowItem
                    href={href}
                    onClick={(e: MouseEvent<HTMLElement>) =>
                        onLinkClick({ e, pageNumber: currentPage + 1 })
                    }
                    dataTn={`page-${type}`}
                >
                    <FormattedMessage id="PageLinks.next" defaultMessage="Next" />
                </PageLinksArrowItem>
            );
        }
        default:
            return null;
    }
};
